import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { AccountInfoContext } from '../account/account-info';
import { tcrBrandSelector } from '@/redux/app/selectors/tcr';
import { orgIdSelector } from '@/redux/app/selectors/organization';
import { getOrgProps } from '@/redux/app/actions/organization';
import { fetchTcrProps } from '@/redux/app/actions/tcr';
import { useNavigationActions } from '@/lib/navigation';
import { TrialPhoneVerifyModal } from '../trial/TrialPhoneVerifyModal';
import { selfThreadPath } from '../trial/use-phone-verification.hook';
import { FunnelState, useTrialFunnel } from '../trial/trial-funnel';

/*
  The trial's single next step in the top bar: verify the phone, then verify the business (A2P), then a quiet status
  link while Upgrade (in the trial chip, TrialStatus) takes over as the green action. Toll-free and expired
  trials show no button here; their only next step is Upgrade.

  The verify step opens TrialPhoneVerifyModal inline so it works on any page. Funnel state reads redux, so
  submitting A2P or getting approved moves the button along without a reload.
*/

interface NextStep {
  label: string;
  // Path under /accounts/:subdomain; no path means the verify-phone modal
  path?: string;
  primary: boolean;
  hint?: string;
}

const NEXT_STEP: Partial<Record<FunnelState, NextStep>> = {
  'verify-phone': { label: 'Verify Phone for $5 Credit', primary: true },
  'apply-a2p': {
    label: 'Verify Business to Start Texting',
    path: 'tcr/first_submission',
    primary: true,
    hint: 'Carriers require A2P 10DLC registration before you can text customers. Your first one is free.',
  },
  'a2p-pending': { label: 'Verification In Review', path: 'tcr', primary: false, hint: 'Carriers usually approve within 1 to 3 business days' },
  'a2p-approved': { label: 'Business Verified', path: 'tcr', primary: false },
  'paid-choose-number': { label: 'Choose a Number', path: 'number', primary: true },
};

export const TrialHeader = () => {
  const { subdomain } = useContext(AccountInfoContext);
  const { state } = useTrialFunnel();
  const tcrBrand = useSelector(tcrBrandSelector);
  const orgId = useSelector(orgIdSelector);
  const { navigate } = useNavigationActions();
  const dispatch = useDispatch();
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // The inbox doesn't load org or TCR props itself, and without them the funnel would wrongly show 'apply-a2p'
  useEffect(() => {
    if (subdomain && !orgId) dispatch(getOrgProps({ subdomain }));
  }, [dispatch, subdomain, orgId]);
  useEffect(() => {
    if (subdomain && !tcrBrand?.id) dispatch(fetchTcrProps({ subdomain }));
  }, [dispatch, subdomain, tcrBrand?.id]);

  if (state === 'hide') return null;

  const step = NEXT_STEP[state];
  const handleStep = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!step.path) {
      setShowVerifyModal(true);
      return;
    }
    navigate({ path: `${window.location.origin}/accounts/${subdomain}/${step.path}`, params: {} });
  };
  const StepButton = step?.primary ? CtaButton : UpgradeButton;

  return (
    <Wrapper>
      {step && (
        <StepButton type="button" title={step.hint} onClick={handleStep}>
          {step.label}
        </StepButton>
      )}
      {state === 'verify-phone' && (
        <TrialPhoneVerifyModal
          show={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          onVerified={(result) => { window.location.href = selfThreadPath(subdomain, result); }}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
`;

export const CtaButton = styled.button`
  background: #19BE51;
  color: #fff;
  border: 1px solid #19BE51;
  padding: 0 18px;
  height: 38px;
  border-radius: 50px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0px 2px 6px rgb(0 0 0 / 25%);
  &:hover { background: #16a847; }
`;

export const UpgradeButton = styled.button`
  background: #fff;
  color: #19BE51;
  border: 1px solid #19BE51;
  padding: 0 18px;
  height: 38px;
  border-radius: 50px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #F1FBF4; }
`;
